import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '@/config/eveBackend';
import { checkUserRole } from '@/utils/adminUtils';
import { isMigrateToStagingEnabled } from '@/utils/featureFlags';

import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import { OrgPickerPanel, type MigratePickerOptions, type OrgPickerOrg } from '@/components/admin/migration/OrgPickerPanel';
import { MigrationRunPanel, type MigrationRun, type MigrationRunEvent, type OrgRunStatus } from '@/components/admin/migration/MigrationRunPanel';
import { AdminNav } from '@/components/admin/AdminNav';

type Org = OrgPickerOrg;

function generateMaskSeed(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_FAILURES = 5;
const VALID_STATUSES = new Set<string>(['running', 'completed', 'failed']);

function parseStatus(raw: unknown): MigrationRun['status'] {
  return VALID_STATUSES.has(raw as string) ? (raw as MigrationRun['status']) : 'failed';
}

interface RunResultShape {
  partial?: { migrated?: string[]; skipped?: string[]; errors?: Record<string, string> };
  error?: string;
  checkpoint?: {
    orgIndex?: number;
    orgIds?: string[];
    mainTableIndex?: number;
    poChildTableIndex?: number;
    connJobIndex?: number;
    connTableIndex?: number;
    connAfterCtid?: string;
    connJobs?: unknown[];
  };
  diagnostics?: {
    lastCopyFailure?: { message?: string; progress?: { activeTable?: string; connAfterCtid?: string | null } };
    lastInvoke?: { message?: string; status?: number };
  };
}

function checkpointSummary(result: RunResultShape | null | undefined): string | undefined {
  const cp = result?.checkpoint;
  if (!cp) return undefined;
  const parts = [
    `orgIndex=${cp.orgIndex ?? 0}/${cp.orgIds?.length ?? 0}`,
    `mainTable=${cp.mainTableIndex ?? 0}`,
    `poChild=${cp.poChildTableIndex ?? 0}`,
    `connJob=${cp.connJobIndex ?? 0}`,
    `connTable=${cp.connTableIndex ?? 0}`,
  ];
  if (cp.connAfterCtid) parts.push(`ctid=${cp.connAfterCtid}`);
  return parts.join(' ');
}

function enrichFromResult(result: RunResultShape | null | undefined): Pick<
  MigrationRun, 'error' | 'checkpointSummary' | 'lastCopyFailure' | 'lastInvoke' | 'hasPartialErrors'
> {
  const errors = result?.partial?.errors ?? {};
  const copy = result?.diagnostics?.lastCopyFailure;
  const invoke = result?.diagnostics?.lastInvoke;
  return {
    error: result?.error,
    checkpointSummary: checkpointSummary(result),
    lastCopyFailure: copy
      ? `${copy.message ?? ''}${copy.progress?.activeTable ? ` @ ${copy.progress.activeTable}` : ''}${copy.progress?.connAfterCtid ? ` ${copy.progress.connAfterCtid}` : ''}`
      : undefined,
    lastInvoke: invoke
      ? `http ${invoke.status ?? '?'} ${invoke.message ?? ''}`
      : undefined,
    hasPartialErrors: Object.keys(errors).length > 0,
  };
}

interface EventQuery {
  select: (cols: string) => EventEq;
}

interface EventEq {
  eq: (col: string, val: string) => EventFilter;
}

interface EventFilter {
  gt: (col: string, val: number) => EventOrder;
  order: (col: string, opts: { ascending: boolean }) => EventLimit;
}

interface EventOrder {
  order: (col: string, opts: { ascending: boolean }) => EventLimit;
}

interface EventLimit {
  limit: (n: number) => Promise<{ data: Array<Record<string, unknown>> | null; error: { message: string } | null }>;
}

function mapRunEvents(data: Array<Record<string, unknown>>): MigrationRunEvent[] {
  return data.map(row => ({
    id: Number(row.id),
    ts: String(row.ts ?? ''),
    level: String(row.level ?? 'info'),
    kind: String(row.kind ?? 'log'),
    message: String(row.message ?? ''),
    orgId: typeof row.org_id === 'string' ? row.org_id : undefined,
  }));
}

/** Initial window for the panel. Incremental polls use `afterId`. */
const EVENT_PAGE_SIZE = 200;

async function fetchRunEvents(runId: string, afterId?: number): Promise<MigrationRunEvent[]> {
  try {
    const query = supabase.from('migration_run_events') as unknown as EventQuery;
    const filtered = query.select('id, ts, level, kind, message, org_id').eq('run_id', runId);
    const ordered = afterId != null
      ? filtered.gt('id', afterId).order('id', { ascending: true })
      : filtered.order('id', { ascending: false });
    const { data, error } = await ordered.limit(EVENT_PAGE_SIZE);
    if (error || !data) return [];
    const events = mapRunEvents(data);
    return afterId != null ? events : events.reverse();
  } catch {
    return [];
  }
}

function buildOrgStatuses(
  runOrgIds: string[],
  migrated: Set<string>,
  skipped: Set<string>,
  errors: Record<string, string>,
  nameMap: Map<string, string>,
): OrgRunStatus[] {
  return runOrgIds.map(id => ({
    orgId: id,
    orgName: nameMap.get(id) ?? id,
    state: migrated.has(id) ? 'completed'
      : skipped.has(id) ? 'skipped'
        : id in errors ? 'failed'
          : 'pending',
    ...(id in errors && { error: errors[id] }),
  }));
}

const StagingMigration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runs, setRuns] = useState<MigrationRun[]>([]);
  const [busyRunId, setBusyRunId] = useState<string | null>(null);
  const pollFailures = useRef<Map<string, number>>(new Map());
  const pollTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pollEpochs = useRef<Map<string, number>>(new Map());
  const activeRunIds = useRef<Set<string>>(new Set());
  const eventCursors = useRef<Map<string, number>>(new Map());
  const isMounted = useRef(true);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }
      const isUserAdmin = await checkUserRole(user.email ?? '', 'admin');
      if (!isUserAdmin) { navigate('/admin'); return; }
    };
    checkAdmin();
  }, [navigate]);

  useEffect(() => {
    supabase
      .from('organisations')
      .select('id, name, connections')
      .order('name')
      .then(({ data, error }) => {
        if (!isMounted.current) return;
        if (error) {
          toast({ title: 'Failed to load organisations', description: error.message, variant: 'destructive' });
        }
        setOrgs(data ?? []);
        setOrgsLoading(false);
      });
  }, [toast]);

  useEffect(() => {
    return () => { pollTimers.current.forEach(t => clearTimeout(t)); };
  }, []);

  const poll = useCallback(async (runId: string, epoch: number) => {
    if (pollEpochs.current.get(runId) !== epoch) return;
    try {
      const { data, error } = await supabase
        .from('migration_runs')
        .select('status, result, created_at, completed_at, org_ids')
        .eq('id', runId)
        .single();

      if (error) throw error;
      if (pollEpochs.current.get(runId) !== epoch) return;

      pollFailures.current.set(runId, 0);

      const result = (data.result ?? null) as RunResultShape | null;
      const rawPartial = result?.partial;

      const migrated = new Set(rawPartial?.migrated ?? []);
      const skipped = new Set(rawPartial?.skipped ?? []);
      const errors = rawPartial?.errors ?? {};
      const runOrgIds = (data.org_ids ?? []) as string[];
      const status = parseStatus(data.status);
      const afterId = eventCursors.current.get(runId);
      const incoming = await fetchRunEvents(runId, afterId);
      if (pollEpochs.current.get(runId) !== epoch) return;
      if (incoming.length) {
        eventCursors.current.set(runId, incoming[incoming.length - 1].id);
      }

      if (!isMounted.current) return;
      if (pollEpochs.current.get(runId) !== epoch) return;
      setRuns(prev => prev.map(r => {
        if (r.runId !== runId) return r;
        const nameMap = new Map(r.orgStatuses.map(os => [os.orgId, os.orgName]));
        return {
          ...r,
          status,
          completedAt: (data.completed_at as string | null) ?? undefined,
          orgStatuses: buildOrgStatuses(runOrgIds, migrated, skipped, errors, nameMap),
          events: afterId == null ? incoming : [...(r.events ?? []), ...incoming],
          ...enrichFromResult(result),
        };
      }));

      if (status === 'running' && activeRunIds.current.has(runId) && pollEpochs.current.get(runId) === epoch) {
        pollTimers.current.set(runId, setTimeout(() => { void poll(runId, epoch); }, POLL_INTERVAL_MS));
      } else {
        activeRunIds.current.delete(runId);
      }
    } catch {
      if (pollEpochs.current.get(runId) !== epoch) return;
      const failures = (pollFailures.current.get(runId) ?? 0) + 1;
      pollFailures.current.set(runId, failures);
      if (!isMounted.current) return;
      if (failures >= MAX_POLL_FAILURES) {
        setRuns(prev => prev.map(r => r.runId === runId ? { ...r, pollError: true } : r));
        activeRunIds.current.delete(runId);
      } else if (activeRunIds.current.has(runId) && pollEpochs.current.get(runId) === epoch) {
        pollTimers.current.set(runId, setTimeout(() => { void poll(runId, epoch); }, POLL_INTERVAL_MS));
      }
    }
  }, []);

  const beginPollLoop = useCallback((runId: string) => {
    const existing = pollTimers.current.get(runId);
    if (existing !== undefined) clearTimeout(existing);
    const epoch = (pollEpochs.current.get(runId) ?? 0) + 1;
    pollEpochs.current.set(runId, epoch);
    activeRunIds.current.add(runId);
    pollFailures.current.set(runId, 0);
    pollTimers.current.set(runId, setTimeout(() => { void poll(runId, epoch); }, POLL_INTERVAL_MS));
  }, [poll]);

  useEffect(() => {
    if (orgsLoading) return;
    supabase
      .from('migration_runs')
      .select('id, status, result, created_at, completed_at, org_ids')
      .order('created_at', { ascending: false })
      .limit(20)
      .then(async ({ data }) => {
        if (!isMounted.current || !data) return;

        const orgNameMap = new Map(orgs.map(o => [o.id, o.name]));
        const loaded: MigrationRun[] = [];
        for (const row of data) {
          const result = (row.result ?? null) as RunResultShape | null;
          const rawPartial = result?.partial;
          const migrated = new Set(rawPartial?.migrated ?? []);
          const skipped = new Set(rawPartial?.skipped ?? []);
          const errors = rawPartial?.errors ?? {};
          const runOrgIds = (row.org_ids ?? []) as string[];
          const status = parseStatus(row.status);
          const events = await fetchRunEvents(row.id as string);
          if (events.length) {
            eventCursors.current.set(row.id as string, events[events.length - 1].id);
          }

          loaded.push({
            runId: row.id as string,
            status,
            startedAt: row.created_at as string,
            completedAt: (row.completed_at as string | null) ?? undefined,
            orgStatuses: buildOrgStatuses(runOrgIds, migrated, skipped, errors, orgNameMap),
            events,
            ...enrichFromResult(result),
          });
        }

        setRuns(loaded);

        loaded.forEach(r => {
          if (r.status === 'running') beginPollLoop(r.runId);
        });
      });
  }, [orgsLoading, orgs, poll, beginPollLoop]);

  const handleSubmit = async (orgIds: string[], options: MigratePickerOptions): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const maskSeed = generateMaskSeed();
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = SUPABASE_URL;
      const apiKey = SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/migrate-organisations-v1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? apiKey}`,
          'apikey': apiKey,
        },
        body: JSON.stringify({
          orgIds,
          options: {
            maskSeed,
            force: options.force,
            dryRun: options.dryRun,
            snapshotsOnly: options.snapshotsOnly,
            snapshotFromDate: options.snapshotFromDate,
            snapshotToDate: options.snapshotToDate,
            includedConnections: options.includedConnections,
          },
        }),
      });

      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `HTTP ${response.status}`);
      }

      const runId = response.headers.get('X-Migration-Run-Id');
      if (!runId) throw new Error('Migration started but no run ID was returned.');

      const orgNameMap = new Map(orgs.map(o => [o.id, o.name]));
      const orgStatuses: OrgRunStatus[] = orgIds.map(id => ({
        orgId: id,
        orgName: orgNameMap.get(id) ?? id,
        state: 'pending',
      }));

      const newRun: MigrationRun = { runId, status: 'running', startedAt: new Date().toISOString(), orgStatuses };
      setRuns(prev => [newRun, ...prev]);
      beginPollLoop(runId);
      return true;
    } catch (err) {
      toast({
        title: 'Migration failed to start',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const postResume = async (runId: string, resumeOverrides?: Record<string, unknown>): Promise<void> => {
    setBusyRunId(runId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/migrate-organisations-v1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ runId, runMode: 'resume', resumeOverrides }),
      });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `HTTP ${response.status}`);
      }
      setRuns(prev => prev.map(run => run.runId === runId ? { ...run, status: 'running', pollError: false } : run));
      beginPollLoop(runId);
    } catch (err) {
      toast({
        title: 'Failed to resume migration',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBusyRunId(null);
    }
  };

  const migratedOrgIds = useMemo(() => {
    const ids = new Set<string>();
    runs.forEach(r => r.orgStatuses.forEach(os => {
      if (os.state === 'completed') ids.add(os.orgId);
    }));
    return ids;
  }, [runs]);

  if (!isMigrateToStagingEnabled()) {
    navigate('/admin', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Migrate to Staging</h1>
          <AdminNav />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OrgPickerPanel
            orgs={orgs.filter(o => !o.name.toLowerCase().startsWith('[test]'))}
            loading={orgsLoading}
            isSubmitting={isSubmitting}
            migratedOrgIds={migratedOrgIds}
            onSubmit={handleSubmit}
          />
          <MigrationRunPanel
            runs={runs}
            busyRunId={busyRunId}
            onResume={runId => { void postResume(runId); }}
            onSkipOrg={runId => { void postResume(runId, { skipCurrentOrg: true }); }}
            onRetryOrg={runId => { void postResume(runId, { retryCurrentOrg: true }); }}
          />
        </div>
      </main>
    </div>
  );
};

export default StagingMigration;
